/* eslint-disable unicorn/prevent-abbreviations, no-plusplus */

import {
    FileTreeDropState,
    FileTreeMoveStatus,
    FileTreeNodeKind
} from './FileTree.enums';
import type { FileTreeDragSource, FileTreeNode } from './FileTree.types';

export const FILE_TREE_FOLDER_MARKER = '.gitkeep';
export const FILE_TREE_PATH_MIME = 'application/x-newo-path';

export const getParentPath = (path: string): string => {
    const separatorIndex = path.lastIndexOf('/');
    return separatorIndex === -1
        ? ''
        : path.slice(0, Math.max(0, separatorIndex));
};

export const joinFileTreePath = (parent: string, name: string): string =>
    parent ? `${parent}/${name}` : name;

export const getFileTreeBasename = (path: string): string => {
    const separatorIndex = path.lastIndexOf('/');
    return separatorIndex === -1
        ? path
        : path.slice(Math.max(0, separatorIndex + 1));
};

export const getFileTreeMoveStatus = (
    source: FileTreeDragSource,
    targetFolder: string
): FileTreeMoveStatus => {
    if (source.path === targetFolder) {
        return FileTreeMoveStatus.Self;
    }
    if (
        source.kind === FileTreeNodeKind.Folder &&
        (targetFolder === source.path ||
            targetFolder.startsWith(`${source.path}/`))
    ) {
        return FileTreeMoveStatus.Descendant;
    }
    if (getParentPath(source.path) === targetFolder) {
        return FileTreeMoveStatus.SameParent;
    }
    return FileTreeMoveStatus.Allowed;
};

export const getFileTreeDropState = (
    isHovered: boolean,
    moveStatus: FileTreeMoveStatus
): FileTreeDropState => {
    if (!isHovered) {
        return FileTreeDropState.Idle;
    }
    if (moveStatus === FileTreeMoveStatus.Allowed) {
        return FileTreeDropState.Valid;
    }
    if (
        moveStatus === FileTreeMoveStatus.Self ||
        moveStatus === FileTreeMoveStatus.SameParent
    ) {
        return FileTreeDropState.Idle;
    }
    return FileTreeDropState.Invalid;
};

export const isFileTreePathTaken = (
    items: { name: string }[] | undefined,
    path: string
): boolean =>
    (items ?? []).some(
        (item) => item.name === path || item.name.startsWith(`${path}/`)
    );

export const hasFileTreeDragData = (dataTransfer: DataTransfer): boolean =>
    Array.from(dataTransfer.types).includes(FILE_TREE_PATH_MIME);

const sortNodes = (nodes: FileTreeNode[]): FileTreeNode[] => {
    nodes.sort((a, b) => {
        if (a.kind !== b.kind) {
            return a.kind === FileTreeNodeKind.Folder ? -1 : 1;
        }
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
    for (const node of nodes) {
        if (node.kind === FileTreeNodeKind.Folder) {
            sortNodes(node.children);
        }
    }
    return nodes;
};

export function parseGCSKeysToTree(keys: string[]): FileTreeNode[] {
    const root: FileTreeNode[] = [];

    for (const key of keys) {
        const parts = key.split('/').filter(Boolean);
        let currentLevel = root;

        let pathPrefix = '';

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            const isFile = i === parts.length - 1;
            const fullPath = pathPrefix ? `${pathPrefix}/${part}` : part;

            if (isFile) {
                if (part === FILE_TREE_FOLDER_MARKER) {
                    continue;
                }
                currentLevel.push({
                    children: [],
                    kind: FileTreeNodeKind.File,
                    name: part,
                    path: key
                });
            } else {
                let folder = currentLevel.find(
                    (node) =>
                        node.kind === FileTreeNodeKind.Folder &&
                        node.name === part
                );
                if (!folder) {
                    folder = {
                        kind: FileTreeNodeKind.Folder,
                        name: part,
                        path: fullPath,
                        children: []
                    };
                    currentLevel.push(folder);
                }
                pathPrefix = fullPath;
                currentLevel = folder.children;
            }
        }
    }

    return sortNodes(root);
}
